import { clamp, ln1p } from '../../core/math';
import type { HistogramData, HistogramSeries, HistogramTone } from '../types';

export type Buckets = Record<string, number[]>;

export const DEFAULT_TONES: HistogramTone[] = [
  { id: 'strong' },
  { id: 'muted' },
];

export const tonesOf = (data: Pick<HistogramData, 'tones'>): HistogramTone[] =>
  data.tones && data.tones.length ? data.tones : DEFAULT_TONES;

export const emptyBuckets = (n: number, tones: HistogramTone[]): Buckets =>
  Object.fromEntries(tones.map((tone) => [tone.id, new Array(n).fill(0)]));

const toneOf = (series: HistogramSeries, tones: HistogramTone[]) =>
  tones.some((t) => t.id === series.tone)
    ? (series.tone as string)
    : tones[tones.length - 1].id;

export const columnOf = (
  t: number,
  { start, end }: Pick<HistogramData, 'start' | 'end'>,
  n: number
) => {
  const span = Math.max(1, end - start);
  return clamp(Math.floor(((t - start) / span) * n), 0, n - 1);
};

export const bucketSeries = (
  data: HistogramData,
  n: number,
  accept: (series: HistogramSeries) => boolean
): Buckets => {
  const tones = tonesOf(data);
  const buckets = emptyBuckets(n, tones);
  for (const series of data.series) {
    if (!accept(series)) continue;
    const columns = buckets[toneOf(series, tones)];
    for (const [t, weight] of series.points) {
      columns[columnOf(t, data, n)] += weight;
    }
  }
  return buckets;
};

const stackHeight = (buckets: Buckets, i: number) =>
  Object.values(buckets).reduce((sum, columns) => sum + ln1p(columns[i]), 0);

export const yScaleFor = (buckets: Buckets, height: number) => {
  const first = Object.values(buckets)[0] ?? [];
  const tallest = first.reduce(
    (max: number, _, i) => Math.max(max, stackHeight(buckets, i)),
    1
  );
  return (height - 1) / tallest;
};

export const segHeight = (weight: number, yScale: number) =>
  weight > 0 ? Math.max(1, ln1p(weight) * yScale) : 0;
